import * as React from "react";
import apiClient from "@/api-client";
// The agent configuration form, reused verbatim. A judge is chosen exactly the way an
// agent's model is, against the same plugins.
import { Service } from "@/new-pages/agents/create/service";
import { Alert, Box, Button, Card, Divider, Grid, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";

import { logger } from "@/lib/default-logger";
import { useProvingGroundSettings, useSaveProvingGroundSettings } from "@/hooks/api/use-proving-ground";
import { ComponentLoader } from "@/components/componentLoader/component-loader";
import { toast } from "@/components/core/toaster";
import { DynamicField } from "@/components/dynamicField/dynamic-field";
import { DynamicFieldType } from "@/components/dynamicField/types";

import { PersonaDefaults } from "./persona-defaults";

export function ProvingGroundSettingsPage(): React.JSX.Element {
	const { data: settings, isLoading } = useProvingGroundSettings();
	const save = useSaveProvingGroundSettings();

	const [plugins, setPlugins] = React.useState<any[]>([]);
	const [judgeFields, setJudgeFields] = React.useState<any[]>([]);
	const [loadingPlugins, setLoadingPlugins] = React.useState(true);

	const [tab, setTab] = React.useState("judge");
	const [languages, setLanguages] = React.useState<string[]>([]);
	const [rateIn, setRateIn] = React.useState("");
	const [rateOut, setRateOut] = React.useState("");
	const [rateTts, setRateTts] = React.useState("");
	const [rateStt, setRateStt] = React.useState("");

	const methods = useForm<any>({
		defaultValues: {
			llm: { pluginId: "", settings: {} },
			defaults: {
				stt: { pluginId: "", settings: {} },
				llm: { pluginId: "", settings: {} },
				tts: { pluginId: "", settings: {} },
			},
			maxCallSeconds: 420,
			concurrency: 4,
		},
	});
	const { control, setValue, getValues, handleSubmit } = methods;

	const loadFields = async (pluginId: string) => {
		if (!pluginId) {
			setJudgeFields([]);
			return;
		}
		try {
			const response = await apiClient.get(`/plugin-services/llm/${pluginId}`);
			setJudgeFields(response.data);

			const current = getValues()?.llm?.settings ?? {};
			setValue(
				"llm.settings",
				Object.fromEntries(
					response.data.map((field: any) => [
						field.name,
						current[field.name] === undefined ? field.value : current[field.name],
					])
				)
			);
		} catch (error) {
			logger.error(error);
			toast.error("Could not load that plugin's settings");
		}
	};

	React.useEffect(() => {
		if (isLoading || !settings) return;

		const load = async () => {
			setLoadingPlugins(true);
			try {
				const response = await apiClient.get("/plugin-services/llm");
				setPlugins(response.data);

				const defaults = settings.defaultPersonaServices ?? {};
				methods.reset({
					llm: { pluginId: settings.judgePluginId ?? "", settings: settings.judgeSettings ?? {} },
					defaults: {
						stt: { pluginId: defaults.stt?.pluginId ?? "", settings: defaults.stt?.settings ?? {} },
						llm: { pluginId: defaults.llm?.pluginId ?? "", settings: defaults.llm?.settings ?? {} },
						tts: { pluginId: defaults.tts?.pluginId ?? "", settings: defaults.tts?.settings ?? {} },
					},
					maxCallSeconds: settings.maxCallSeconds,
					concurrency: settings.concurrency,
				});

				setLanguages(settings.defaultPersonaLanguages ?? []);
				const rates = settings.costRates ?? {};
				setRateIn(rates.llm?.in === undefined ? "" : String(rates.llm.in));
				setRateOut(rates.llm?.out === undefined ? "" : String(rates.llm.out));
				setRateTts(rates.tts?.perMillionChars === undefined ? "" : String(rates.tts.perMillionChars));
				setRateStt(rates.stt?.perMinute === undefined ? "" : String(rates.stt.perMinute));

				if (settings.judgePluginId) await loadFields(settings.judgePluginId);
			} catch (error) {
				logger.error(error);
				toast.error("Could not load the available models");
			} finally {
				setLoadingPlugins(false);
			}
		};

		void load();
	}, [isLoading, settings?.judgePluginId]);

	const onSubmit = async (values: any) => {
		// Only services that actually have a plugin are stored, or a caller would inherit an
		// empty provider and fail at the first turn with nothing explaining why.
		const defaults: Record<string, { pluginId: string; settings: Record<string, any> }> = {};
		for (const service of ["stt", "llm", "tts"]) {
			const pluginId = values.defaults?.[service]?.pluginId;
			if (pluginId) defaults[service] = { pluginId, settings: values.defaults[service].settings ?? {} };
		}

		await save.mutateAsync({
			judgePluginId: values.llm?.pluginId || null,
			judgeSettings: values.llm?.settings ?? {},
			defaultPersonaServices: defaults,
			defaultPersonaLanguages: languages,
			// Each meter is stored only when priced, so a blank field means "unknown" rather than
			// "free" and the run can say which parts of its figure are real.
			costRates: {
				...(rateIn || rateOut ? { llm: { in: Number(rateIn) || 0, out: Number(rateOut) || 0 } } : {}),
				...(rateTts ? { tts: { perMillionChars: Number(rateTts) } } : {}),
				...(rateStt ? { stt: { perMinute: Number(rateStt) } } : {}),
			},
			maxCallSeconds: Number(values.maxCallSeconds),
			concurrency: Number(values.concurrency),
		});
	};

	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={4}>
				<Box>
					<Typography variant="h4">Proving Ground settings</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						Which model grades your calls, and how hard runs are allowed to push.
					</Typography>
				</Box>

				<Card sx={{ position: "relative" }}>
					<ComponentLoader loading={isLoading || loadingPlugins} />
					<Tabs value={tab} onChange={(_event, value: string) => setTab(value)} sx={{ px: 2 }} variant="scrollable">
						<Tab value="judge" label="Judge" sx={{ minHeight: "auto" }} />
						<Tab value="personas" label="Persona defaults" sx={{ minHeight: "auto" }} />
						<Tab value="limits" label="Limits & cost" sx={{ minHeight: "auto" }} />
					</Tabs>
					<Divider />
					<Box sx={{ p: 2.5 }}>
						<FormProvider {...methods}>
							<Stack spacing={3}>
								<Box sx={{ display: tab === "judge" ? "block" : "none" }}>
									<Typography variant="h6" sx={{ mb: 0.5 }}>
										Judge model
									</Typography>
									<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
										Grades every LLM-judged metric across the account. Deterministic and statistical metrics do not use
										it.
									</Typography>

									<Alert severity="warning" sx={{ mb: 2 }}>
										Worth choosing a different model from the agents you are testing. A judge sharing an agent&rsquo;s
										model shares its blind spots, and tends to accept exactly the answers that model would have given.
									</Alert>

									<Service
										control={control}
										service="llm"
										plugins={plugins}
										onPluginChange={(_service: string, pluginId: string) => loadFields(pluginId)}
										settings={judgeFields}
									/>
								</Box>

								<Box sx={{ display: tab === "personas" ? "block" : "none" }}>
									<PersonaDefaults languages={languages} onLanguagesChange={setLanguages} />
								</Box>

								<Box sx={{ display: tab === "limits" ? "block" : "none" }}>
									<Typography variant="h6" sx={{ mb: 2 }}>
										Run limits
									</Typography>
									<Grid container spacing={2}>
										<Grid size={{ md: 6, xs: 12 }}>
											<DynamicField
												control={control}
												type={DynamicFieldType.NUMBER}
												name="maxCallSeconds"
												label="Maximum call length (seconds)"
												dynamicField={{
													name: "maxCallSeconds",
													type: DynamicFieldType.NUMBER,
													label: "Maximum call length (seconds)",
													description:
														"A caller and an agent that misunderstand each other can talk indefinitely. In voice mode this burns wall clock as well as money.",
												}}
											/>
										</Grid>
										<Grid size={{ md: 6, xs: 12 }}>
											<DynamicField
												control={control}
												type={DynamicFieldType.NUMBER}
												name="concurrency"
												label="Calls at once"
												dynamicField={{
													name: "concurrency",
													type: DynamicFieldType.NUMBER,
													label: "Calls at once",
													description:
														"Voice calls hold a transcription socket open for their whole duration, so this is bounded by your provider's limits rather than by our servers.",
												}}
											/>
										</Grid>
									</Grid>

									<Divider sx={{ my: 3 }} />

									<Typography variant="h6" sx={{ mb: 0.5 }}>
										Pricing
									</Typography>
									<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
										Runs record what they consumed. Each provider bills in different units, so each is priced separately
										— anything left blank is simply excluded, and the run says what its figure covers.
									</Typography>

									<Alert severity="info" sx={{ mb: 2 }}>
										A voice run costs far more than its models alone. Both sides hold a transcription connection open
										for the whole call, so a one-minute call is billed as two transcription minutes.
									</Alert>

									<Grid container spacing={2}>
										<Grid size={{ md: 6, xs: 12 }}>
											<TextField
												label="Models — input, $ per million tokens"
												value={rateIn}
												onChange={(event) => setRateIn(event.target.value)}
												placeholder="0.30"
												fullWidth
											/>
										</Grid>
										<Grid size={{ md: 6, xs: 12 }}>
											<TextField
												label="Models — output, $ per million tokens"
												value={rateOut}
												onChange={(event) => setRateOut(event.target.value)}
												placeholder="2.50"
												fullWidth
											/>
										</Grid>
										<Grid size={{ md: 6, xs: 12 }}>
											<TextField
												label="Speech — $ per million characters"
												value={rateTts}
												onChange={(event) => setRateTts(event.target.value)}
												placeholder="100"
												helperText="ElevenLabs and most synthesis bill per character of text, not per token."
												fullWidth
											/>
										</Grid>
										<Grid size={{ md: 6, xs: 12 }}>
											<TextField
												label="Transcription — $ per minute"
												value={rateStt}
												onChange={(event) => setRateStt(event.target.value)}
												placeholder="0.0048"
												helperText="Deepgram streaming bills connected time, not speech time."
												fullWidth
											/>
										</Grid>
									</Grid>
								</Box>

								<Box>
									<Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={save.isPending}>
										Save settings
									</Button>
								</Box>
							</Stack>
						</FormProvider>
					</Box>
				</Card>
			</Stack>
		</Box>
	);
}
